import {
  AuditLogRowShell,
  LogTimestampCell,
} from '@/features/AuditLogs/components/AuditLogsTable/AuditLogRowShell'
import {
  type AuditLogTableProps,
  AuditLogTableShell,
} from '@/features/AuditLogs/components/AuditLogsTable/AuditLogTableShell'
import {
  formatLicenseAction,
  formatLicenseStatus,
  getLicenseAction,
  getLicenseStatusColor,
  getLicenseStatusColorPalette,
} from '@/features/Licenses/lib/licenseHistory'
import type { AuditLogs } from '@/generated/http-clients/v3ApiSchemas'
import { Badge, Table, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

/**
 * License-tailored variant of the audit-log table. The generic audit table
 * shows a `resource` column, which for license events is always
 * `{ id, type: 'license' }` and never says what happened. This surfaces the
 * actual action (Created / Renewed / Revoked / ...) from the audit metadata
 * instead, and keeps the status + timestamp + user columns. The table shell
 * (states, date grouping) is shared with the audit-logs table.
 */
export function LicenseHistoryTable({
  logs,
  isLoading,
  isError,
  isEmpty,
  skeletonWidths,
  selectedLogId,
  onRowClick,
  errorComponent,
  emptyMessage,
}: Readonly<AuditLogTableProps>) {
  const { t } = useTranslation('features', { useSuspense: false })

  return (
    <AuditLogTableShell
      logs={logs}
      isLoading={isLoading}
      isError={isError}
      isEmpty={isEmpty}
      skeletonWidths={skeletonWidths}
      errorComponent={errorComponent}
      emptyMessage={emptyMessage}
      // status indicator + action + timestamp + user + status
      columnCount={5}
      ariaLabel={String(t('licenses.detail.history.tableLabel'))}
      headerRow={
        <Table.Row>
          {/* Status indicator column - no header */}
          <Table.ColumnHeader width="4px" p={0} />
          <Table.ColumnHeader>
            {t('licenses.detail.history.columns.action')}
          </Table.ColumnHeader>
          <Table.ColumnHeader whiteSpace="nowrap" width="auto">
            {t('licenses.detail.history.columns.timestamp')}
          </Table.ColumnHeader>
          <Table.ColumnHeader width="220px">
            {t('licenses.detail.history.columns.user')}
          </Table.ColumnHeader>
          <Table.ColumnHeader>
            {t('licenses.detail.history.columns.status')}
          </Table.ColumnHeader>
        </Table.Row>
      }
      renderRow={(log, showDate) => (
        <LicenseHistoryRow
          log={log}
          isSelected={log.id === selectedLogId}
          onClick={() => onRowClick(log)}
          showDate={showDate}
        />
      )}
    />
  )
}

interface LicenseHistoryRowProps {
  log: AuditLogs
  isSelected: boolean
  onClick: () => void
  showDate?: boolean
}

function LicenseHistoryRow({
  log,
  isSelected,
  onClick,
  showDate = true,
}: Readonly<LicenseHistoryRowProps>) {
  const { t } = useTranslation('features', { useSuspense: false })

  const actionLabel = formatLicenseAction(getLicenseAction(log), t)
  const statusText = formatLicenseStatus(log.status, t)

  return (
    <AuditLogRowShell
      statusColor={getLicenseStatusColor(log.status)}
      isSelected={isSelected}
      onClick={onClick}
    >
      <Table.Cell>
        <Text fontSize="sm" fontWeight="medium" lineClamp="1">
          {actionLabel}
        </Text>
      </Table.Cell>
      <LogTimestampCell timestamp={log.timestamp} showDate={showDate} />
      <Table.Cell>
        <Text fontSize="sm" lineClamp="1">
          {log.user || '-'}
        </Text>
      </Table.Cell>
      <Table.Cell>
        <Badge
          colorPalette={getLicenseStatusColorPalette(log.status)}
          size="sm"
        >
          {statusText}
        </Badge>
      </Table.Cell>
    </AuditLogRowShell>
  )
}
